import { Link } from "react-router-dom";
import Title from "components/Title";
import { RootState } from "redux/store";
import { useNavigate } from "react-router-dom";
import useToggle from "hooks/useToggle";
import Spinner from "components/Spinner";
import { useEffect, useState, useRef, useMemo } from "react";
import { Card, Col, Row, Form, Button } from "react-bootstrap";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { getStores } from "redux/slices/Stores";
import { TooltipIcon } from "components";
import { StoresApi } from "apis/StoresApi";
import { AgGridReact } from "ag-grid-react";
import { ColDef, IRowNode, RowNode } from "ag-grid-enterprise";
import { Store, StoreLockDownView } from "models/dbModels/stores/Store";
import { ZIcon } from "components/ZIcon";
import { ZIconType } from "helpers/utility/iconMap";
import { Background } from "ag-grid-enterprise/dist/lib/ag-charts-community/module-support";
import "./storeLockDownStyle.css";
import { ZMultiSelectDropdown } from "components/ZMultiSelectDropdown";

export interface Node {
	title: string;
	key?: string;
	group?: string;
	tooltip?: string;
	children?: Node[];
}

export const LockDownFieldsList: Node[] = [
	{
		title: "Stock",
		group: "Stock",
		children: [
			{
				title: "General",
				group: "Stock_General",
				children: [
					{ title: "Name", key: "Stock.Name" },
					{ title: "Supplier", key: "Stock.Supplier" },
					{ title: "Department", key: "Stock.Department" },
					{ title: "Sub Department", key: "Stock.SubDepartment" },
					{ title: "Manufacturer", key: "Stock.Manufacturer" },
					{ title: "Stock Group", key: "Stock.StockGroup" },
					{ title: "Location", key: "Stock.Location" },
					{ title: "Category", key: "Stock.Category" },
					{ title: "Pricing Policy", key: "Stock.PricingPolicy" },
					{ title: "Rounding Policy", key: "Stock.RoundingPolicy" },

					{ title: "Carton Size", key: "Stock.CartonSize" },
					{ title: "Min Stock Level", key: "Stock.MinStockLevel" },
					{ title: "Max Stock Level", key: "Stock.MaxStockLevel" },
					{ title: "Min Order Qty", key: "Stock.MinOrderQty" },

					{ title: "Active", key: "Stock.Active" },
					{ title: "Auto Orders", key: "Stock.OrderStock" },
					{
						title: "Order Only if SOH < Min Stock Level",
						key: "Stock.OnlyOrderWhenBelowMinimum",
					},
					{ title: "Stock Line Ethical", key: "Stock.Ethical" },
					{ title: "Stock Line Discountable", key: "Stock.Discountable" },
					{ title: "Auth Require to sell", key: "Stock.AuthReq" },
					{ title: "Set Bulk Food Pricing", key: "Stock.SoldByWeight" },
					{ title: "Desc. On Cust. Display", key: "Stock.DisplayDescription" },
					{ title: "Stock is marked for Online", key: "Stock.IsOnline" },

					{ title: "Sell Price", key: "Stock.SellPrice" },
					{ title: "RRP", key: "Stock.Rrp" },
					{ title: "GST Code", key: "Stock.GSTCode" },
					{
						title: "Additional Ordering Info",
						key: "Stock.AdditionalOrderingInfo",
					},
				],
			},
			{
				title: "Additional Details",
				group: "Stock_Details",
				children: [
					{ title: "Full Stock Name", key: "Stock.FullStockName" },
					{ title: "Height", key: "Stock.Height" },
					{ title: "Length", key: "Stock.length" },
					{ title: "Width", key: "Stock.Width" },
					{ title: "Weight", key: "Stock.Weight" },
					{ title: "Volume", key: "Stock.Volume" },
					{
						title: "Max Purchases Per Customer",
						key: "Stock.MaxPurchasesPerCustomer",
					},
					{ title: "Additional Details", key: "Stock.AdditionalDetails" },
				],
			},
			{
				title: "PDE",
				group: "Stock_Pde",
				children: [
					{ title: "Add", key: "Stock.Pde.Add" },
					{ title: "Delete", key: "Stock.Pde.Delete" },
					{ title: "Edit", key: "Stock.Pde.Edit" },
					{ title: "Default", key: "Stock.Pde.Default" },
					{ title: "Edit Codes", key: "Stock.Pde.EditCodes" },
				],
			},
			{
				title: "APN",
				group: "Stock_Apn",
				children: [
					{ title: "Add", key: "Stock.Apn.Add" },
					{ title: "Delete", key: "Stock.Apn.Delete" },
					{ title: "Edit", key: "Stock.Apn.Edit" },
					{ title: "Default", key: "Stock.Apn.Default" },
				],
			},
		],
	},
];

export interface LockDownModel extends Record<string, any> {
	name: string;
	key: string;
}

export const BulkLockDownFields = [
	{ display: "All Stock", key: "Stock", level: 0, disabled: true },
	{ display: "General", key: "General", level: 1, disabled: false },
	{ display: "Additional Details", key: "Additional Details", level: 1, disabled: false },
	{ display: "PDE", key: "PDE", level: 1, disabled: false },
	{ display: "APN", key: "APN", level: 1, disabled: false },
];

export function StoreLockdownFields() {
	const form = useRef(null);
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const [isValidated, setValidated] = useToggle();
	const [loading, setLoading] = useState<boolean>(false);
	const store = useAppSelector((state: RootState) => state.stores);
	const [LockDownFields, setLockDownFields] = useState<
		{ id: number; fields: string[] }[]
	>([]);
	const currentStoreGroupId = useAppSelector(
		(state: RootState) => state.global.storeGroupId
	);
	const stockTableRef = useRef<AgGridReact>(null);
	const [rows, setRows] = useState<any[]>([]);
	const user = useAppSelector((state: RootState) => state.logedinUser);

	const [selectedBulkLockDownFields, setSelectedBulkLockDownFields] = useState<
		string[]
	>([]);

	function loadStoreData() {
		dispatch(getStores({ storeGroupIds: [currentStoreGroupId] })).then(
			(res) => {
				if (res.data?.length > 0) {
					setLockDownFields(
						res.data.map((x) => ({
							id: x.id,
							fields: x.lockDownFields?.split(",") ?? [],
						}))
					);

					let rowData = loadLockDownModels(LockDownFieldsList, res.data, 0, {});
					setRows(rowData);
				}
			}
		);
	}

	function loadLockDownModels(
		fields: Node[],
		stores: Store[],
		depth: number,
		parent?: any
	): LockDownModel[] {
		let rowData: LockDownModel[] = [];
		fields.forEach((f) => {
			if (f.children && f.children.length > 0) {
				//Has no key
				rowData.push(
					...loadLockDownModels(f.children, stores, depth + 1, {
						...parent,
						[`group${depth}`]: f.title,
					})
				);
			} else {
				let row: LockDownModel = {
					name: f.title,
					key: f.key ?? f.title,
					...parent,
				};

				if (f.key) {
					stores.forEach((x) => {
						row[x.id.toString()] = x.lockDownFields?.split(",").includes(f.key!) ?? false;
					});
				}
				rowData.push(row);
			}
		});
		return rowData;
	}

	const columns = useMemo(() => {
		let gridColumns: ColDef[] = [];

		//Add the groups
		let maxDepth = 0;
		function getMaxDepth(nodes: Node[], depth: number) {
			nodes.forEach((n) => {
				if (n.children) {
					getMaxDepth(n.children, depth + 1);
				}
			});
			maxDepth = Math.max(maxDepth, depth);
		}
		getMaxDepth(LockDownFieldsList, 0);

		for (let i = 0; i < maxDepth; i++) {
			gridColumns.push({
				field: `group${i}`,
				rowGroup: true,
				hide: true,
				suppressHeaderMenuButton: true,
				sortable: false,
			});
		}

		gridColumns.push({
			headerName: "Categories",
			field: "name",
			minWidth: 300,
			showRowGroup: true,
			cellRenderer: "agGroupCellRenderer",
			pinned: "left",
			cellRendererParams: {
				suppressCount: true,
			}, 
			sortable: false,
			suppressHeaderMenuButton: true,
			cellStyle: (params) => {
				//If cell is a group, make it bold, otherwise normal
				if (params.node.group) {
					return {
						"font-weight": "bold",
						background: "#e3f4f4",
					};
				}
			},
		});

		//Create the columns of stores
		store.table.forEach((s) => {
			gridColumns.push({
				headerName: s.name,
				field: s.id.toString(),
				minWidth: 200,
				flex: 1,
				suppressHeaderMenuButton: true,
				sortable: false,
				aggFunc: (params) => {
					const values = params.values.filter((i) => i !== null);
					if (values.length > 0) {
						return values.reduce((a, b) => a && b, true);
					}
					return false;
				},
				cellStyle: (params) => {
					//If cell is a group, make it bold, otherwise normal
					if (params.node.group) {
						return { background: "#e3f4f4" };
					}
				},
				cellRenderer: (params: any) => {
					return (
						<>
							<Button
								variant={params.value ? "primary" : "light"}
								disabled={!user.permissions.lockDownFields.canUpdate}
								onClick={() => {
									console.log(params);
									//If group then set all children to the same value
									if (params.node.group) {
										params.node.allLeafChildren.forEach((c: RowNode) => {
											c.setDataValue(params.colDef.field, !params.value);
										});
									} else {
										params.setValue(!params.value);
									}
								}}
								size="sm"
								style={{
									marginLeft: `${params.node.level}rem`,
									marginRight: "1rem",
									padding: "0rem 0.25rem",
								}}
							>
								<ZIcon icon={params.value ? ZIconType.Lock : ZIconType.Unlock} variant={params.value ? "" : "gray"} />
							</Button>
							{params.node.group ? " All" : ""}
						</>
					);
				},
			});
		});
		return gridColumns;
	}, [store.table]);

	useEffect(() => {
		if (user.permissions.lockDownFields.canView) {
			loadStoreData();
		}
	}, [user.permissions.lockDownFields.canView]);

	function updateStore() {
		setLoading(true);
		let fields: Record<string, string[]> = store.table.reduce((acc: any, s) => {
			acc[s.id.toString()] = [];
			return acc;
		}, {});

		stockTableRef.current?.api.forEachNode((node) => {
			if (!node.group) {
				store.table.forEach((s) => {
					if (node.data[s.id.toString()]) {
						fields[s.id].push(node.data.key);
					}
				});
			}
		});

		let sendInfo: StoreLockDownView[] = Object.keys(fields).map((key) => ({
			id: parseInt(key),
			lockDownFields: fields[key].join(","),
		}));

		StoresApi.UpdateLockDownFields(sendInfo).finally(() => {
			loadStoreData();
			setLoading(false);
		});
	}

	function handleSubmit() {
		// @ts-ignore
		if (form.current.checkValidity() === true) {
			updateStore();
		}
		setValidated();
	}

	function bulkUpdate() {
		if (selectedBulkLockDownFields.length === 0) {
			return;
		}

		function anyParentWithKey(node: IRowNode<any>, key: string): boolean {
			if (node.parent && node.parent.groupData) {
				if (node.parent.groupData.name === key) {
					return true;
				}
				return anyParentWithKey(node.parent, key);
			}
			return false;
		}

		selectedBulkLockDownFields.forEach((f) => {
			stockTableRef.current?.api.forEachNode((node) => {
				if (!node.group && anyParentWithKey(node, f)) {
					store.table.forEach((s) => {
						node.setDataValue(s.id.toString(), true);
					});
				}
			});
		});
	}

	return (
		<>
			<Title pageTitle={"Lockdown"} parent="" />
			<Row>
				<Col xs={12}>
					<Card>
						<Card.Body>
							<Row className="mb-2">
								<Col xs={12} md={6} xl={3}>
									<ZMultiSelectDropdown
										label="Lock all stores by:"
										options={BulkLockDownFields}
										values={BulkLockDownFields.filter((x) =>
											selectedBulkLockDownFields.includes(x.key)
										)}
										value={(p) => p.key}
										display={(p) => " ".repeat(p.level) + p.display}
										disabled={(p) => p.disabled}
										onSelectionChange={(p) =>

											setSelectedBulkLockDownFields(p.map((x) => x.key))
										}
										placeholder="🔒 Select..."
										hasAllOption={true}
										itemClassName={(p) => `ms-${p.level * 2}`}
										hideCheckbox={(p) => p.disabled}
										dropdownIsDisabled={!user.permissions.lockDownFields.canUpdate}
									/>
								</Col>
								<Col xs={3} style={{ alignSelf: "end", paddingLeft: '0px' }}>
									<Button
										variant="outline-primary"
										className="mt-2"
										onClick={bulkUpdate}
										disabled={!user.permissions.lockDownFields.canUpdate}
									>
										Apply
									</Button>
								</Col>
							</Row>

							<Row className="mb-1">
								<AgGridReact
									ref={stockTableRef}
									suppressNoRowsOverlay={loading}
									columnDefs={columns}
									rowData={rows}
									rowSelection="single"
									className={`ag-theme-quartz ag-theme-custom w-100 flex-grow ag-custom-indent`}
									containerStyle={{ height: "65dvh" }}
									suppressRowClickSelection
									groupAllowUnbalanced
									suppressCellFocus
									animateRows
									suppressContextMenu
									stopEditingWhenCellsLoseFocus={true}
									groupDisplayType="custom"
									groupDefaultExpanded={-1}
									suppressAggFuncInHeader
									suppressRowHoverHighlight
									suppressRowDrag
									suppressAggFilteredOnly
									suppressMovableColumns

								/>

								<Form validated={isValidated} ref={form}>
									<Row className="mt-3">
										<Col sm={6}>
											<Link
												to="/app/stores"
												className="btn text-muted d-none d-sm-inline-block btn-link fw-semibold"
											>
												<i className="mdi mdi-arrow-left"></i> Back to All
												Stores{" "}
											</Link>
										</Col>
										<Col sm={6} className="d-flex flex-row-reverse">
											<Button
												variant="primary"
												className="btn cta-button"
												disabled={loading || !user.permissions.lockDownFields.canUpdate || !user.permissions.lockDownFields.canView}
												onClick={handleSubmit}
											>
												{loading ? (
													<>
														<Spinner
															className="spinner-border-sm me-1"
															tag="span"
															color="white"
														/>
													</>
												) : (
													<></>
												)}
												{loading ? <>Saving...</> : <>Save</>}
											</Button>
										</Col>
									</Row>
								</Form>
							</Row>
						</Card.Body>
					</Card>
				</Col>
			</Row>
		</>
	);
}
